import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowLeft } from "lucide-react";
import BookCard from "@/components/BookCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Ella Raine cosmic palette
const ER = {
  bg: "#05050A",
  surface: "#0A0B15",
  card: "#111222",
  cyan: "#00E5FF",
  violet: "#B026FF",
  ivory: "#F0F4F8",
  muted: "#8B95A5",
  border: "#1F223D",
};

export default function EllaRaine() {
  const [info, setInfo] = useState(null);
  const [books, setBooks] = useState([]);
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 700], [0, 180]);

  useEffect(() => {
    axios.get(`${API}/pseudonyms/ella-raine`).then((r) => setInfo(r.data)).catch(() => {});
    axios
      .get(`${API}/books`, { params: { pseudonym: "Ella Raine" } })
      .then((r) => setBooks(r.data))
      .catch(() => {});
  }, []);

  if (!info) {
    return (
      <div className="page-wrapper-dark pt-40 px-6">
        <div className="text-[#8B95A5] uppercase tracking-[0.32em] text-xs animate-pulse">Loading…</div>
      </div>
    );
  }

  return (
    <div className="page-wrapper-dark" data-testid="ella-raine-page" style={{ backgroundColor: ER.bg, color: ER.ivory }}>
      {/* HERO */}
      <section className="relative min-h-[80vh] flex items-center overflow-hidden">
        <motion.div style={{ y: heroY }} className="absolute inset-0">
          <div className="absolute inset-0" style={{
            background: `radial-gradient(circle at 20% 30%, ${ER.cyan}30, transparent 50%), radial-gradient(circle at 80% 70%, ${ER.violet}30, transparent 55%), ${ER.bg}`,
          }} />
          <div className="absolute inset-0" style={{
            background: `linear-gradient(135deg, ${ER.bg} 0%, ${ER.surface} 50%, ${ER.bg} 100%)`,
            opacity: 0.7,
          }} />
          {/* Subtle grid for cosmic vibe */}
          <div className="absolute inset-0 opacity-[0.04]" style={{
            backgroundImage: `linear-gradient(${ER.cyan} 1px, transparent 1px), linear-gradient(90deg, ${ER.cyan} 1px, transparent 1px)`,
            backgroundSize: "80px 80px",
          }} />
        </motion.div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-24 w-full">
          <Link
            to="/about"
            data-testid="back-to-author"
            className="inline-flex items-center gap-2 text-sm transition-colors mb-12 hover:opacity-80"
            style={{ color: ER.muted }}
          >
            <ArrowLeft size={14} /> Back to Ellie Stone
          </Link>

          <div className="max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-6 text-xs uppercase font-medium"
              style={{ color: ER.cyan, letterSpacing: "0.34em" }}
            >
              ✦ Sci-Fi Romance · The Cosmic Imprint ✦
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.15 }}
              className="font-serif text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-light leading-[1.02] tracking-tight mb-6"
              style={{ color: ER.ivory }}
            >
              Ella{" "}
              <span className="italic" style={{ background: `linear-gradient(135deg, ${ER.cyan}, ${ER.violet})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                Raine
              </span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="font-serif italic text-2xl md:text-3xl leading-snug mb-8 max-w-2xl"
              style={{ color: ER.ivory, opacity: 0.92 }}
            >
              "{info.tagline}"
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.45 }}
              className="text-base md:text-lg leading-relaxed max-w-2xl"
              style={{ color: ER.muted }}
            >
              {info.long_bio}
            </motion.p>
          </div>
        </div>
      </section>

      {/* READER ADVISORY BAND */}
      <section
        className="py-10"
        style={{
          backgroundColor: ER.surface,
          borderTop: `1px solid ${ER.border}`,
          borderBottom: `1px solid ${ER.border}`,
        }}
      >
        <div className="max-w-5xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
          <div>
            <div className="mb-2 text-xs uppercase font-medium" style={{ color: ER.cyan, letterSpacing: "0.34em" }}>
              Reader Advisory
            </div>
            <p className="text-sm leading-relaxed max-w-xl" style={{ color: ER.ivory, opacity: 0.8 }}>
              Books under Ella Raine lean into sci-fi romance with explicit content, cosmic-scale stakes, and morally questionable choices. Not recommended for younger or sensitive readers.
            </p>
          </div>
          <Link
            to="/books"
            data-testid="cta-main-library"
            className="text-sm whitespace-nowrap transition-colors underline underline-offset-4"
            style={{ color: ER.muted, textDecorationColor: `${ER.cyan}66` }}
          >
            Prefer the witchier side? →
          </Link>
        </div>
      </section>

      {/* BOOKS */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-16">
            <div className="mb-5 text-xs uppercase font-medium" style={{ color: ER.cyan, letterSpacing: "0.34em" }}>
              The Cosmic Imprint
            </div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] max-w-3xl" style={{ color: ER.ivory }}>
              Books written between <span className="italic">the stars</span>.
            </h2>
          </div>

          {books.length === 0 ? (
            <p style={{ color: ER.muted }}>No titles available yet.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {books.map((b, i) => (
                <BookCard key={b.id} book={b} index={i} cosmic />
              ))}
            </div>
          )}

          <div
            className="mt-20 pt-16 flex flex-col md:flex-row items-center justify-between gap-6"
            style={{ borderTop: `1px solid ${ER.border}` }}
          >
            <p className="text-sm max-w-md" style={{ color: ER.muted }}>
              Want notice when Ella Raine sends something out of orbit? The Letter covers both pen names.
            </p>
            <Link
              to="/#newsletter"
              data-testid="cta-newsletter"
              className="inline-flex items-center gap-2 px-7 py-3 rounded-full uppercase tracking-[0.22em] text-xs font-medium transition-all duration-300 hover:scale-105"
              style={{
                background: `linear-gradient(135deg, ${ER.cyan}, ${ER.violet})`,
                color: ER.bg,
                boxShadow: `0 8px 24px ${ER.cyan}33`,
              }}
            >
              Join the Letter <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
